import { useCallback, useEffect, useState } from 'react';
import { isFunction, isUndefined } from 'lodash';
import { Logger } from '../../utilities/logger';

const SESSION_STORAGE_CHANGE = 'sessionStorageChange';

export type UseSessionStorageOptions = {
  /**
   * If session has no value for `key`, copy from localStorage once (e.g. first load after migrating drawer persistence).
   */
  migrateFromLocalOnce?: boolean;
};

/**
 * Persists React state per browser tab (`sessionStorage`), unlike {@link useLocalStorage} which is shared across tabs.
 */
export const useSessionStorage = <T = any>(key: string, initialValue?: T, options?: UseSessionStorageOptions) => {
  const { migrateFromLocalOnce = false } = options ?? {};

  const readFromSessionStorage = (): T | undefined => {
    try {
      let item = window.sessionStorage.getItem(key);
      if ((item === null || item === '') && migrateFromLocalOnce) {
        const migrated = window.localStorage.getItem(key);
        if (migrated !== null && migrated !== '') {
          window.sessionStorage.setItem(key, migrated);
          item = migrated;
        }
      }
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      Logger.debug(`Error reading sessionStorage key "${key}":`, error);
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(() => readFromSessionStorage() as T);

  // Reload when the key changes so a mounted consumer does not keep the previous key's value.
  useEffect(() => {
    setStoredValue(readFromSessionStorage() as T);
    // initialValue is the first-paint fallback; rehydrate only when the key changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail.key === key) {
        setStoredValue(e.detail.value);
      }
    };

    window.addEventListener(SESSION_STORAGE_CHANGE, handleCustomStorageChange as EventListener);

    return () => {
      window.removeEventListener(SESSION_STORAGE_CHANGE, handleCustomStorageChange as EventListener);
    };
  }, [key]);

  const setValue = useCallback(
    (value: T | ((prevValue: T) => T)) => {
      try {
        setStoredValue(prevStoredValue => {
          const valueToStore = isFunction(value) ? value(prevStoredValue) : value;

          if (!isUndefined(valueToStore)) {
            window.sessionStorage.setItem(key, JSON.stringify(valueToStore));

            window.dispatchEvent(
              new CustomEvent(SESSION_STORAGE_CHANGE, {
                detail: { key, value: valueToStore },
              })
            );

            return valueToStore;
          }

          return prevStoredValue;
        });
      } catch (error) {
        Logger.debug(`Error setting sessionStorage key "${key}":`, error);
      }
    },
    [key]
  );

  const getCurrentValue = useCallback((): T => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : storedValue;
    } catch (error) {
      Logger.debug(`Error reading current sessionStorage key "${key}":`, error);
      return storedValue;
    }
  }, [key, storedValue]);

  return [storedValue, setValue, getCurrentValue] as const;
};

export default useSessionStorage;
